import { useEffect, useState, memo } from "react";
import L from "leaflet";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { useDispatch, useSelector } from "react-redux";
import { setFieldShipping } from "../actions/checkoutStore";

const STADIA_API_KEY = "739e5f98-582d-490c-bd09-78511fcb2cc2";

// Component to fit the map to all markers
function FitBoundsToMarkers({ markers }) {
  const map = useMap();

  useEffect(() => {
    if (markers.length > 0) {
      const bounds = L.latLngBounds(markers.map((marker) => marker.latlng));
      map.fitBounds(bounds, { padding: [15, 15] });
    }
  }, [markers, map]);

  return null;
}

const PickupLocationsMap = memo(function PickupLocationsMap({
  pickupPoints,
  colors,
}) {
  const dispatch = useDispatch();

  const [selectedMarker, setSelectedMarker] = useState(null);

  // Transform the pickupPoints array into the format needed for markers
  const markers = pickupPoints
    .filter((pickupPoint) => pickupPoint.location_can_fulfill_order)
    .map((pickupPoint) => ({
      id: pickupPoint.id,
      latlng: [pickupPoint.address.latitude, pickupPoint.address.longitude],
      pickupPointName: pickupPoint.name,
    }));

  const pickupPointId = useSelector(
    (state) => state.checkoutStore.shipping.pickup_point_id,
  );

  // Function to fetch the SVG content and dynamically modify its color
  function getSvgWithColor(svgContent, color) {
    const modifiedSvg = svgContent.replace(/fill="grey"/g, `fill="${color}"`);
    return `data:image/svg+xml;base64,${btoa(modifiedSvg)}`;
  }

  const Box = `<svg width="49" height="54" viewBox="0 0 49 54" fill="none" xmlns="http://www.w3.org/2000/svg">
  <g filter="url(#filter0_d_1_11)">
  <path fill-rule="evenodd" clip-rule="evenodd" d="M27.4103 42.795C37.3538 41.3818 45 32.8338 45 22.5C45 11.1782 35.8218 2 24.5 2C13.1782 2 4 11.1782 4 22.5C4 32.8338 11.6462 41.3818 21.5897 42.795C21.5975 42.9831 21.6599 43.174 21.7876 43.346L23.697 45.9183C24.0969 46.4569 24.9031 46.4569 25.303 45.9183L27.2124 43.346C27.3401 43.174 27.4025 42.9831 27.4103 42.795Z" fill="white"/>
  </g>
  <path d="M17.925 15.0067C18.2335 14.3897 18.8642 14 19.5544 14H23.2857L22.0714 18.8571H16L17.925 15.0067ZM26.9286 18.8571L25.7143 14H29.4444C30.135 14 30.765 14.3897 31.0761 15.0067L33 18.8571H26.9286ZM22.0714 22.5C22.0714 23.1717 22.6141 23.7143 23.2857 23.7143H25.7143C26.3859 23.7143 26.9286 23.1717 26.9286 22.5V20.0714H33V28.5714C33 29.9109 31.9109 31 30.5714 31H18.4286C17.0872 31 16 29.9109 16 28.5714V20.0714H22.0714V22.5Z" fill="grey"/>
  <defs>
  <filter id="filter0_d_1_11" x="0" y="1" width="49" height="52.3223" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
  <feFlood flood-opacity="0" result="BackgroundImageFix"/>
  <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
  <feOffset dy="3"/>
  <feGaussianBlur stdDeviation="2"/>
  <feComposite in2="hardAlpha" operator="out"/>
  <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
  <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_1_11"/>
  <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_1_11" result="shape"/>
  </filter>
  </defs>
  </svg>
  `;

  const faDefaultIconUrl = getSvgWithColor(Box, "grey");
  const faSelectedtIconUrl = getSvgWithColor(Box, colors.main_color);

  const defaultIcon = new L.Icon({
    iconUrl: faDefaultIconUrl,
    iconSize: [41, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });

  const selectedIcon = new L.Icon({
    iconUrl: faSelectedtIconUrl,
    iconSize: [50, 50],
    iconAnchor: [15, 46],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });

  const handleMarkerClick = (marker) => {
    setSelectedMarker(marker.id);
    dispatch(setFieldShipping("pickup_point_id", marker.id));
    dispatch(setFieldShipping("pickup_point_name", marker.pickupPointName)); // Set the pickupPointName correctly
  };

  useEffect(() => {
    if (pickupPointId) {
      setSelectedMarker(pickupPointId);
    }
  }, [pickupPointId]);

  const mapKey = `${new Date().getTime()}`;

  return (
    <MapContainer
      key={mapKey}
      center={[0, 0]}
      zoom={1}
      dragging={true}
      scrollWheelZoom={true}
      zoomControl={true}
      style={{ height: "35vh", width: "100%" }}
      attributionControl={false}
    >
      <TileLayer
        url={`https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png?api_key=${STADIA_API_KEY}`}
        attribution='&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>, &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors'
      />
      {markers.map((marker) => (
        <Marker
          key={marker.id}
          position={marker.latlng}
          icon={selectedMarker === marker.id ? selectedIcon : defaultIcon}
          eventHandlers={{
            click: () => handleMarkerClick(marker),
          }}
        />
      ))}
      <FitBoundsToMarkers markers={markers} />
    </MapContainer>
  );
});

export default PickupLocationsMap;
